import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  X,
  Trash2,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  DollarSign,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import { payrollAPI } from "@/lib/api";

export default function DeletePayslipModal({ payslip, onClose, onDeleted }) {
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(false);

  const getStatusInfo = (status) => {
    const statusConfig = {
      brouillon: {
        label: "Brouillon",
        color: "bg-gray-100 text-gray-800",
        icon: AlertCircle,
        description: "En cours de préparation",
        canDelete: true,
        message: "Ce bulletin peut être supprimé en toute sécurité.",
      },
      valide: {
        label: "Validé",
        color: "bg-green-100 text-green-800",
        icon: CheckCircle,
        description: "Approuvé et prêt pour paiement",
        canDelete: true,
        message:
          "Ce bulletin peut être supprimé, mais la validation sera perdue.",
      },
      paye: {
        label: "Payé",
        color: "bg-blue-100 text-blue-800",
        icon: DollarSign,
        description: "Paiement effectué",
        canDelete: false,
        message:
          "Impossible de supprimer un bulletin déjà payé pour des raisons de conformité.",
      },
    };
    return statusConfig[status] || statusConfig.brouillon;
  };

  const handleDelete = async () => {
    setLoading(true);

    try {
      await payrollAPI.deletePayslip(payslip.id);

      showSuccess("Bulletin de paie supprimé avec succès");
      onDeleted(payslip.id);
      onClose();
    } catch (error) {
      console.error("Error deleting payslip:", error);
      const errorMessage =
        error.response?.data?.error || "Erreur lors de la suppression";
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!payslip) return null;

  const statusInfo = getStatusInfo(payslip.statut);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Supprimer le Bulletin
              </h2>
              <p className="text-gray-600">Action irréversible</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Employee Info */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-600">
                    {payslip.employe?.prenom?.[0]}
                    {payslip.employe?.nom?.[0]}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {payslip.employe?.prenom} {payslip.employe?.nom}
                  </p>
                  <p className="text-sm text-gray-500">
                    {payslip.employe?.numero_employe}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payslip Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Détails du Bulletin</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Période:</span>
                <span className="font-medium">
                  {new Date(payslip.periode_debut).toLocaleDateString("fr-FR")}{" "}
                  - {new Date(payslip.periode_fin).toLocaleDateString("fr-FR")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Salaire Net:</span>
                <span className="font-medium text-green-600">
                  {parseFloat(payslip.salaire_net || 0).toLocaleString(
                    "fr-MA",
                    {
                      style: "currency",
                      currency: "MAD",
                    }
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Statut:</span>
                <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Warning Message */}
          <Card
            className={`border-l-4 ${
              statusInfo.canDelete
                ? "border-amber-400 bg-amber-50"
                : "border-red-400 bg-red-50"
            }`}
          >
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <statusInfo.icon
                  className={`h-5 w-5 mt-0.5 ${
                    statusInfo.canDelete ? "text-amber-600" : "text-red-600"
                  }`}
                />
                <div>
                  <h4
                    className={`font-medium ${
                      statusInfo.canDelete ? "text-amber-800" : "text-red-800"
                    }`}
                  >
                    {statusInfo.canDelete
                      ? "Attention"
                      : "Suppression Impossible"}
                  </h4>
                  <p
                    className={`text-sm mt-1 ${
                      statusInfo.canDelete ? "text-amber-700" : "text-red-700"
                    }`}
                  >
                    {statusInfo.message}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Warnings */}
          {statusInfo.canDelete && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center space-x-2 text-gray-700">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Considérations importantes:
                </span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1 ml-6">
                <li>• Cette action est irréversible</li>
                <li>• Toutes les données du bulletin seront perdues</li>
                <li>• L'historique des modifications sera supprimé</li>
                <li>
                  • Assurez-vous d'avoir sauvegardé les informations importantes
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          {statusInfo.canDelete ? (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              <Trash2 className="h-4 w-4" />
              <span>
                {loading ? "Suppression..." : "Supprimer définitivement"}
              </span>
            </Button>
          ) : (
            <Button
              variant="outline"
              disabled
              className="flex items-center space-x-2"
            >
              <AlertCircle className="h-4 w-4" />
              <span>Suppression impossible</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
